<template>
  <nav
    class="navbar is-fixed-top"
    :class="{
      'is-scrolled': isScrolled
    }"
    role="navigation"
    aria-label="main navigation"
  >
    <div class="container">
      <div class="navbar-brand">
        <router-link
          to="/"
          class="navbar-item"
        >
          <div class="logo">
            <img
              v-if="isDarkMode"
              src="/src/assets/images/logo-white.png"
              alt="Indiefy Logo"
              height="28"
            >
            <img
              v-else
              src="/src/assets/images/logo-dark.png"
              alt="Indiefy Logo"
              height="28"
            >
          </div>
        </router-link>

        <a
          role="button"
          class="navbar-burger burger"
          :class="{ 'is-active': isMenuOpen }"
          aria-label="menu"
          aria-expanded="false"
          @click="toggleMenu"
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </a>
      </div>

      <div
        class="navbar-menu"
        :class="{ 'is-active': isMenuOpen }"
      >
        <div class="navbar-start">
          <router-link
            v-if="isAuthenticated"
            to="/dashboard"
            class="navbar-item"
            :class="{ 'is-active': $route.path.startsWith('/dashboard') }"
          >
            {{ $t('navigation.dashboard') }}
          </router-link>

          <router-link
            v-if="isAuthenticated"
            to="/distributions"
            class="navbar-item"
            :class="{ 'is-active': $route.path.startsWith('/distributions') }"
          >
            {{ $t('navigation.releases') }}
          </router-link>

          <router-link
            v-if="isAuthenticated"
            to="/analytics"
            class="navbar-item"
            :class="{ 'is-active': $route.path.startsWith('/analytics') }"
          >
            {{ $t('navigation.analytics') }}
          </router-link>

          <router-link
            v-if="isAuthenticated"
            to="/tools"
            class="navbar-item"
            :class="{ 'is-active': $route.path.startsWith('/tools') }"
          >
            {{ $t('navigation.tools') }}
          </router-link>
        </div>

        <div class="navbar-end">
          <div class="navbar-item action-buttons">
            <!-- Upgrade button for authenticated free users -->
            <router-link
              v-if="isAuthenticated && !isPremiumUser"
              to="/upgrade?from=v3_navbar"
              class="upgrade-button"
              :class="{ 'is-active': $route.path === '/upgrade?from=v3_navbar' }"
            >
              <i class="fas fa-crown" />
              <span>{{ $t('settings.upgrade') }}</span>
            </router-link>

            <!-- Theme toggle button -->
            <button
              v-if="isPremiumUser"
              class="theme-toggle"
              @click="toggleDarkMode"
            >
              <i :class="isDarkMode ? 'fas fa-sun' : 'fas fa-moon'" />
            </button>

            <!-- Authentication buttons -->
            <template v-if="!isAuthenticated">
              <router-link
                to="/auth/login"
                class="auth-button login-button"
              >
                {{ $t('auth.sign-in') }}
              </router-link>

              <router-link
                to="/auth/signup"
                class="auth-button signup-button"
              >
                {{ $t('auth.sign-up') }}
              </router-link>
            </template>
          </div>

          <div
            v-if="isAuthenticated"
            class="navbar-item has-dropdown"
            :class="{ 'is-active': isLanguageDropdownOpen }"
          >
            <a
              class="navbar-link"
              @click="toggleLanguageDropdown"
            >
              <i class="fas fa-globe mr-3" />
              <span class="current-lang">{{ currentLanguageDisplay }}</span>
            </a>

            <div class="navbar-dropdown is-right">
              <a
                v-for="language in availableLanguages"
                :key="language.code"
                class="navbar-item"
                :class="{ 'is-active': currentLanguage === language.code }"
                @click="changeLanguage(language.code)"
              >
                {{ language.name }}
              </a>
            </div>
          </div>

          <div
            v-if="isAuthenticated"
            class="navbar-item has-dropdown"
            :class="{ 'is-active': isAccountDropdownOpen }"
          >
            <a
              class="navbar-link"
              @click="toggleAccountDropdown"
            >
              {{ $t('navigation.account') }}
            </a>

            <div class="navbar-dropdown is-right">
              <!-- <a
                href="https://indiefy.net/dashboard?classic=1"
                class="navbar-item"
              >
                <i class="fa fa-arrow-right mr-3" /> Indiefy Classic
              </a> -->

              <a
                v-if="currentUser && currentUser.impersonate"
                class="navbar-item"
                href="https://indiefy.net/v1/auth/back-to-admin"
              >
                <i class="fa fa-arrow-left mr-3" /> Back to Admin
              </a>

              <router-link
                v-if="isAuthenticated"
                to="/royalties"
                class="navbar-item"
                :class="{ 'is-active': $route.path.startsWith('/royalties') }"
              >
                <i class="fa fa-money-bill-trend-up mr-3" />{{ $t('navigation.royalties') }}
              </router-link>

              <router-link
                to="/account"
                class="navbar-item"
                @click="closeDropdowns"
              >
                <i class="fa fa-gear mr-3" />{{ $t('navigation.settings') }}
              </router-link>
              <hr class="navbar-divider">
              <a
                class="navbar-item"
                @click="logout"
              >
                <i class="fa fa-sign-out-alt mr-3" />{{ $t('auth.sign-out') }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useThemeStore } from '@/store/theme';
import { useAuthStore } from '@/store/auth';
import { getAvailableLanguages, setLanguage } from '@/i18n';
import { useI18n } from 'vue-i18n';

// Router
const router = useRouter();

// Auth store
const authStore = useAuthStore();
const isAuthenticated = computed(() => authStore.isAuthenticated);
const currentUser = computed(() => authStore.currentUser);
const isPremiumUser = computed(() => authStore.currentUser && authStore.currentUser.subscription);

// Theme store
const themeStore = useThemeStore();
const isDarkMode = computed(() => themeStore.isDark);

// Menu state
const isMenuOpen = ref(false);
const isScrolled = ref(false);
const isLanguageDropdownOpen = ref(false);
const isAccountDropdownOpen = ref(false);

// i18n
const { locale } = useI18n();
const currentLanguage = computed(() => locale.value);
const availableLanguages = getAvailableLanguages();
const currentLanguageDisplay = computed(() => {
  const lang = availableLanguages.find(lang => lang.code === currentLanguage.value);
  return lang ? lang.name : 'EN';
});

// Methods
const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
  // Close dropdowns when toggling menu
  if (!isMenuOpen.value) {
    closeDropdowns();
  }
};

const toggleLanguageDropdown = () => {
  isLanguageDropdownOpen.value = !isLanguageDropdownOpen.value;
  // Close other dropdowns
  if (isLanguageDropdownOpen.value) {
    isAccountDropdownOpen.value = false;
  }
};

const toggleAccountDropdown = () => {
  isAccountDropdownOpen.value = !isAccountDropdownOpen.value;
  // Close other dropdowns
  if (isAccountDropdownOpen.value) {
    isLanguageDropdownOpen.value = false;
  }
};

const closeDropdowns = () => {
  isLanguageDropdownOpen.value = false;
  isAccountDropdownOpen.value = false;
};

const toggleDarkMode = () => {
  themeStore.toggleDarkMode();
};

const changeLanguage = (lang) => {
  setLanguage(lang);
  closeDropdowns();
};

const logout = async () => {
  await authStore.logout();
  router.push('/');
};

// Scroll handler
const handleScroll = () => {
  isScrolled.value = window.scrollY > 20;
};

// Close dropdowns when clicking outside
const handleClickOutside = (event) => {
  const dropdowns = document.querySelectorAll('.navbar-item.has-dropdown');
  let clickedInside = false;

  dropdowns.forEach(dropdown => {
    if (dropdown.contains(event.target)) {
      clickedInside = true;
    }
  });

  if (!clickedInside) {
    closeDropdowns();
  }
};

// Lifecycle hooks
onMounted(() => {
  window.addEventListener('scroll', handleScroll);
  document.addEventListener('click', handleClickOutside);
  handleScroll(); // Initial check
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped lang="scss">
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/animations";
@import "@/styles/abstracts/breakpoints";

.navbar {
  transition: $transition-base;
  padding: 0.75rem 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--color-border, rgba(0, 0, 0, 0.1));
  z-index: 30; // Ensure navbar is above content

  .logo {
    max-height: 28px;
    transition: $transition-base;
    display: flex;
    align-items: center;

    img {
      max-height: 28px;
      transition: transform 0.3s ease;
    }
  }

  &.is-scrolled {
    padding: 0.4rem 0;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

    .logo {
      img {
        transform: scale(0.9);
      }
    }
  }

  .navbar-menu {
    @media screen and (max-width: $tablet) {
      position: absolute;
      width: 100%;
      left: 0;
      right: 0;
      top: 100%;
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
      border-bottom: 1px solid var(--color-border, rgba(0, 0, 0, 0.1));
      background-color: var(--color-card-background, white);

      .body--dark & {
        background-color: var(--color-card-background, #1e293b);
        border-color: rgba(255, 255, 255, 0.1);
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
      }
    }
  }

  .navbar-item {
    font-weight: 500;
    transition: all 0.2s ease;
    color: var(--color-text, #333);

    .body--dark & {
      color: var(--color-text, #e2e8f0);
    }

    &:hover, &.is-active {
      color: var(--color-primary, $indiefy-blue);
    }

    &.is-active {
      font-weight: 600;
    }

    &.has-dropdown {
      .navbar-link {
        background-color: transparent;
        color: var(--color-text, #333);
        cursor: pointer;

        .body--dark & {
          color: var(--color-text, #e2e8f0);
        }

        &:after {
          transition: transform 0.2s ease;
        }
      }

      &.is-active .navbar-link:after {
        transform: rotate(135deg);
      }

      &:hover .navbar-link {
        background-color: var(--color-background-alt, rgba(0, 0, 0, 0.03));
      }

      .navbar-dropdown {
        background-color: var(--color-card-background, white);
        border-color: var(--color-border);
        border-radius: 6px;
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
        display: none;

        .body--dark & {
          background-color: var(--color-card-background, #1e293b);
          border-color: rgba(255, 255, 255, 0.1);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
        }

        .navbar-item {
          padding: 0.75rem 1rem;
          color: var(--color-text, #333);

          .body--dark & {
            color: var(--color-text, #e2e8f0);
          }

          &:hover {
            background-color: var(--color-background-alt);
          }
        }
      }

      &.is-active .navbar-dropdown {
        display: block;
      }
    }
  }

  // Action buttons styling
  .action-buttons {
    display: flex;
    align-items: center;
    gap: 0.75rem;

    // Theme toggle button
    .theme-toggle {
      background: transparent;
      border: none;
      color: var(--color-text);
      font-size: 1.2rem;
      padding: 0.4rem;
      border-radius: 50%;
      height: 2.4rem;
      width: 2.4rem;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background-color: var(--color-background-alt);
        color: var(--color-primary);
        transform: rotate(15deg);
      }
    }

    // Language selector
    .language-selector {
      .language-button {
        background: transparent;
        border: none;
        color: var(--color-text);
        font-size: 1rem;
        padding: 0.4rem 0.6rem;
        border-radius: 1.5rem;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .current-lang {
          font-size: 0.9rem;
          font-weight: 500;
        }

        &:hover {
          background-color: var(--color-background-alt);
          color: var(--color-primary);
        }
      }

      .dropdown-item {
        &.is-active {
          background-color: var(--color-primary);
          color: white;
        }

        &:hover:not(.is-active) {
          background-color: var(--color-background-alt);
          color: var(--color-primary);
        }
      }
    }

    // Upgrade button
    .upgrade-button {
      margin-right: 0.5rem;
      color: white !important;
    }

    // Auth buttons
    .auth-button {
      border-radius: 1.5rem;
      padding: 0.4rem 1rem;
      font-weight: 500;
      font-size: 0.9rem;
      transition: all 0.2s ease;

      &.login-button {
        background-color: transparent;
        color: var(--color-text);
        border: 1px solid var(--color-border);

        &:hover {
          background-color: var(--color-background-alt);
          border-color: var(--color-border-hover);
          transform: translateY(-2px);
        }
      }

      &.signup-button {
        background: linear-gradient(45deg, $indiefy-blue, $indiefy-lavender);
        color: white;
        border: none;
        font-weight: 600;

        &:hover {
          box-shadow: 0 4px 12px rgba($indiefy-blue, 0.3);
          transform: translateY(-2px);
        }
      }
    }

    // New release button
    .new-release-button {
      background: linear-gradient(45deg, $indiefy-blue, $indiefy-lavender);
      color: white;
      border: none;
      font-weight: 600;
      border-radius: 1.5rem;
      padding: 0.5rem 1.25rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      transition: all 0.2s ease;

      i {
        font-size: 0.9rem;
      }

      &:hover {
        box-shadow: 0 4px 12px rgba($indiefy-blue, 0.3);
        transform: translateY(-2px);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }
}
</style>